import {
    useState,
    useEffect
} from 'react';
import {
    collection,
    query,
    where,
    getDocs,
    orderBy as firestoreOrderBy,
    limit as firestoreLimit,
    startAt as firestoreStartAt,
    FirestoreError,
    DocumentSnapshot,
    QueryDocumentSnapshot,
    DocumentData,
} from 'firebase/firestore';

import type { ListFilters, ListOrderBy } from '#types';
import { firestore } from '../../services/startFirestore';
import { DEFAULT_SEARCH_MAX_RECORDS } from '#types';

/**
 * Parameters for the useList hook.
 */
export interface UseListParams<T> {
    /** Firestore collection path (e.g., 'users' or 'forms/123/fields') */
    path: string;
    /** Array of filter conditions to apply to the query */
    filters?: ListFilters;
    /** Array of fields to order results by with direction (asc/desc) */
    orderBy?: ListOrderBy;
    /** Maximum number of documents to fetch (defaults to DEFAULT_SEARCH_MAX_RECORDS) */
    limit?: number;
    /** Cursor for pagination - either a numeric value or a DocumentSnapshot */
    startAt?: number | DocumentSnapshot;
    /** Whether to execute the query. If false, returns empty docs without fetching */
    enabled?: boolean;
    /** Optional function to transform raw Firestore document snapshots into typed data. If omitted, returns raw QueryDocumentSnapshot array. */
    transformDocs?: (docs: QueryDocumentSnapshot<DocumentData>[]) => T[];
}

/**
 * Result of the list documents operation.
 */
export interface DocList<T> {
    /** Array of documents - transformed data if transformDocs provided, otherwise raw QueryDocumentSnapshot objects */
    docs: T[];
    /** Error object if the query failed, null otherwise */
    error: Error | null;
}

/**
 * Fetches a list of documents from a Firestore collection with optional filtering, ordering, and pagination.
 * Fetches once on mount when enabled. Does not auto-refetch on parameter changes.
 * 
 * @template T - Type extending DocDataWithId for the returned documents
 * @param params - Configuration object for the list query
 * @returns DocList object containing docs array and error state
 * 
 * @example
 * ```tsx
 * const { docs, error } = useList({
 *   path: 'users',
 *   filters: [{ field: 'active', operator: '==', value: true }],
 *   orderBy: [{ field: 'createdAt', direction: 'desc' }],
 *   limit: 50,
 *   transformDocs: (docs) => docs.map(doc => ({
 *     docId: doc.id,
 *     ...doc.data(),
 *     fullName: `${doc.data().firstName} ${doc.data().lastName}`
 *   }))
 * });
 * ```
 */
export const useList = <T>({
    path,
    filters,
    orderBy,
    limit = DEFAULT_SEARCH_MAX_RECORDS,
    startAt,
    enabled = true,
    transformDocs
}: UseListParams<T>): DocList<T> => {
    const [docs, setDocs] = useState<T[]>([]);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!enabled) return;

        let isCancelled = false;

        const fetchData = async () => {
            try {
                const collectionRef = collection(firestore, path);

                const querySnapshot = await getDocs(
                    query(
                        collectionRef,
                        ...[
                            ...(filters?.map(f => where(f.field, f.operator, f.value)) ?? []),
                            ...(orderBy?.map(o => firestoreOrderBy(o.field, o.direction)) ?? []),
                            ...(limit !== undefined ? [firestoreLimit(limit)] : []),
                            ...(startAt !== undefined ? [firestoreStartAt(startAt)] : [])
                        ].filter(Boolean)
                    )
                );

                if (isCancelled) return;

                setDocs(
                    transformDocs
                        ? transformDocs(querySnapshot.docs)
                        : querySnapshot.docs as unknown as T[]
                );
            } catch (err) {
                if (isCancelled) return;
                const firestoreError = err as FirestoreError;
                console.error(`Error fetching list from ${path}:`, firestoreError);
                setError(new Error(`Failed to fetch list: ${firestoreError.message}`));
            }
        };

        fetchData();

        return () => { isCancelled = true; };
    }, [enabled, firestore, path]); // eslint-disable-line react-hooks/exhaustive-deps

    return { docs, error };
};
